/**
 * Sign-in screen. Single Google button — Firebase popup.
 *
 * Failure modes we surface:
 *   - User closes the popup → silent (no toast, just re-enable the
 *     button). Closing is intentional, not an error.
 *   - Popup blocked by browser → friendly "allow popups" message.
 *   - Anything else → surface the error message verbatim. We don't try
 *     to translate Firebase error codes; the curator audience is small
 *     and the raw message ("Network request failed", etc.) is usually
 *     more useful than a remapped one.
 */
import { useState } from 'react';
import { FirebaseError } from 'firebase/app';

import { signInWithGoogle } from '../lib/firebase';

export default function SignIn() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSignIn = async () => {
    setBusy(true);
    setError(null);
    try {
      await signInWithGoogle();
      // No-op — onAuthStateChanged in App.tsx will pick up the new
      // user and render the next screen.
    } catch (err) {
      if (err instanceof FirebaseError) {
        if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
          // User closed the popup deliberately. No error UI.
          return;
        }
        if (err.code === 'auth/popup-blocked') {
          setError('Your browser blocked the sign-in popup. Allow popups for this site and try again.');
          return;
        }
        setError(err.message);
        return;
      }
      setError(err instanceof Error ? err.message : 'Sign-in failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6">
      <div className="w-full rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="flex flex-col items-center text-center">
          <span className="inline-block h-14 w-14 rounded-2xl bg-soi-500 p-2">
            <svg viewBox="0 0 64 64" className="h-10 w-10">
              <path d="M12 32 H52 A20 20 0 0 1 12 32 Z" fill="#fff7ed" />
              <path
                d="M12 32 H52"
                stroke="#7c2d12"
                strokeWidth="2.5"
                strokeLinecap="round"
                fill="none"
              />
              <path
                d="M22 22 q3 -6 6 0 t6 0 t6 0"
                stroke="#fff"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <h1 className="mt-4 text-xl font-semibold text-slate-900">OpenSoi Curator</h1>
          <p className="mt-2 text-sm text-slate-600">Moderator-only stall submission queue.</p>
        </div>
        <button
          type="button"
          onClick={handleSignIn}
          disabled={busy}
          className="mt-6 flex w-full items-center justify-center gap-3 rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
            />
          </svg>
          {busy ? 'Signing in…' : 'Sign in with Google'}
        </button>
        {error !== null && (
          <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}
        <p className="mt-6 text-center text-xs text-slate-500">
          Need access? Email{' '}
          <a
            className="font-medium text-soi-600 hover:text-soi-700"
            href="mailto:dean.taler@sentra.io?subject=OpenSoi%20moderator%20access"
          >
            a project admin
          </a>
          .
        </p>
      </div>
    </div>
  );
}
